import React from 'react';
import { Avatar, Result } from 'antd';
import Checkbox from '@pages/DesignSystem/Checkbox/Checkbox';
import MultiSelect from '@pages/DesignSystem/Multiselect/MultiSelect';
import DatePickers from '@pages/DesignSystem/date-flat-picker';
import Inputbox from '@pages/DesignSystem/Input-Field';
import EdenredButtons from '@pages/DesignSystem/button/EdenredButton';
import EDragger from '@pages/DesignSystem/Dragger';
import MultiUploadDragger from '@pages/DesignSystem/Dragger/MultiUploadDragger';
import ETypo from '@pages/DesignSystem/Typo';
import ImageComponent from '@sharedComponent/image-component';
import { images } from '@assets/img';
import { icons } from 'src/assets/icons';

import './compliance-violations.scss';

const ComplianceViolationsPresentational = ({
	complianceData,
	previousJustificationDocuments = [],
	laborContractFile = null,
	showCommentSection = false,
	justificationOptions = [],
	isSubmitLoading = false,
	uploadStatus = 'new',
	isFormValid = false,
	dateError = '',
	laborContractFileError = '',
	rejectedLaborContractFileName = null,
	justificationFiles = [],
	justificationFilesError = '',
	requiresJustificationDocument = false,
	requiredDocumentsList = [],
	isRFI = true,
	loadError = false,
	onRetry,
	onBackToEmployees,
	onContractTypeChange,
	onDateChange,
	onJustificationChange,
	onJustificationOptionsChange,
	onGoBack,
	onSubmit,
	onFileUpload,
	onFileRemove,
	onDownloadDocument,
}) => {
	if (loadError) {
		return (
			<Result
				className="load-data-retry"
				status="error"
				title="Failed to load data"
				subTitle="Please try again or return to the employee list."
				extra={[
					<div className="load-data-retry__buttons">
						<EdenredButtons key="retry" btnType="primary" onClick={onRetry}>
							Retry
						</EdenredButtons>
						<EdenredButtons key="back" btnType="secondary" onClick={onBackToEmployees}>
							Back to employees
						</EdenredButtons>
					</div>
				]}
			/>
		);
	}

	return (
		<div className="compliance-violations-container">
			<div className="compliance-header">
				<div className="header-content">
					<div className="title-section">
						<ETypo h2 bold className="page-title">
							Compliance Breaches
						</ETypo>
						<ETypo b1 className="page-subtitle">
							Please provide the necessary information below{showCommentSection ? ' to keep the card.' : '.'}
						</ETypo>
					</div>
					<div className="header-actions">
						<EdenredButtons
							btnType="secondary"
							onClick={onGoBack}
							leftIcon={<ImageComponent src={images.leftsarrow} />}
							className="back-button">
							Back
						</EdenredButtons>
						<EdenredButtons
							btnType="primary"
							onClick={onSubmit}
							className="submit-button"
							loading={isSubmitLoading}
							disabled={isSubmitLoading || !isFormValid}
						>
							Submit
						</EdenredButtons>
					</div>
				</div>
			</div>

			<div className="compliance-content">
				<div className="employee-info-section">
					<div className="employee-card">
						<div className="employee-avatar">
							<Avatar size={48} icon={<ImageComponent src={icons.focus_state.User} />} />
						</div>
						<div className="employee-details">
							<ETypo h3 medium className="employee-name">
								{complianceData.employeeName}
							</ETypo>
							<ETypo b2 light className="employee-id">
								{complianceData.employeeId}
							</ETypo>
						</div>
					</div>
				</div>

				{showCommentSection && (
					<div className="edenred-comments-section">
						<div className="section-header">
							<ETypo className="section-title">
								Edenred Comments
							</ETypo>
						</div>
						<div className="comments-content">
							<div className="comments-text">
								{complianceData.comments[0]}
							</div>
						</div>
						<div className="justification-input-section">
							<div className="textarea-container">
								<ETypo b2 medium className="field-label">
									Enter your justification{' '}
									<ETypo b3 medium color="#ff4d4f">
										*
									</ETypo>
								</ETypo>
								<Inputbox
									placeholder="Type Here"
									value={complianceData.justification || ''}
									onChange={(e) => onJustificationChange(e.target.value)}
									className="justification-input-field"
									maxLength={120}
									required={true}
									inputType="textarea"
								/>
								<div className="character-count">{(complianceData.justification || '').length}/120 Characters</div>
							</div>
						</div>
					</div>
				)}

				<div className="form-section">
					<div className="section-title">
						<ETypo bold>
							Upload MOHRE Labour Contract{' '}
							<ETypo b3 medium color="#ff4d4f">
								*
							</ETypo>
						</ETypo>
					</div>

					<div className="form-row">
						<div className="form-left">
							<div className="form-group">
								<ETypo b2 medium className="field-label" style={{ marginBottom: '16px', display: 'block' }}>
									Contract Type{' '}
									<ETypo b3 medium color="#ff4d4f">
										*
									</ETypo>
								</ETypo>
								<div className="contract-type-checkboxes">
									<Checkbox
										label="Limited Contract"
										checked={complianceData.contractType === 'Limited'}
										onChange={() => onContractTypeChange('Limited')}
										size={20}
										labelStyle={{ marginLeft: '12px', fontSize: '14px' }}
									/>
									<Checkbox
										label="Unlimited Contract"
										checked={complianceData.contractType === 'Unlimited'}
										onChange={() => onContractTypeChange('Unlimited')}
										size={20}
										labelStyle={{ marginLeft: '12px', fontSize: '14px' }}
									/>
								</div>
							</div>

							<div className="form-group">
								<ETypo b2 medium className="field-label">
									Contract Expiry Date{' '}
									{complianceData.contractType === 'Limited' && (
										<ETypo b3 medium color="#ff4d4f" style={{ fontSize: '18px' }}>
											*
										</ETypo>
									)}
								</ETypo>
								<DatePickers
									placeholder="Select Date"
									onChange={onDateChange}
									value={complianceData.contractExpiryDate || ''}
									dateFormat="d/m/Y"
									className="date-picker-field"
								/>
								{dateError && (
									<ETypo b3 className="error-msg" style={{ color: '#ff4d4f', marginTop: '8px' }}>
										{dateError}
									</ETypo>
								)}
							</div>
						</div>

						<div className="form-right">
							<div className="upload-area">
								<div className="upload-header">Upload MOHRE Labour Contract</div>
								<EDragger
									draggerStatus={uploadStatus === 'error' ? 'error' : (laborContractFile ? 'done' : uploadStatus)}
									fileInfo={uploadStatus === 'error' && rejectedLaborContractFileName
										? { name: rejectedLaborContractFileName, error: { message: laborContractFileError } }
										: (laborContractFile ? {
											name: laborContractFile.name || laborContractFile.fileName,
											id: laborContractFile.id,
											size: laborContractFile.size,
										} : null)}
									successMessage={laborContractFile?.name || laborContractFile?.fileName}
									onChange={(e) => onFileUpload(e, 'labor-contract')}
									onRemoveUpload={() => {
										onFileRemove(undefined, 'labor-contract');
									}}
									onDownload={laborContractFile?.id && !laborContractFile?.size ?
										() => onDownloadDocument(laborContractFile.id, laborContractFile.name || laborContractFile.fileName) :
										null
									}
									acceptedTypes=".pdf,.doc,.docx,.jpg,.jpeg,.png,.PDF,.DOC,.DOCX,.JPG,.JPEG,.PNG"
									idleText1="Drag and drop"
									idleText2="or click here"
									fileMaxSize="Maximum file size: 5MB"
									className="compliance-dragger"
								/>
							</div>
							{laborContractFileError && (
								<ETypo b3 className="error-msg" style={{ color: '#ff4d4f', marginTop: '8px' }}>
									{laborContractFileError}
								</ETypo>
							)}
						</div>
					</div>
				</div>

				<div className="justification-section">
					<div className="section-title">
						<ETypo bold>
							Justify this breach
						</ETypo>
					</div>

					<div className="form-row">
						<div className="form-left">
							<div className="form-group">
								<ETypo b2 medium className="field-label">
									Select Your Justification
									{!isRFI && (
										<>
											{' '}
											<ETypo b3 medium color="#ff4d4f">
												*
											</ETypo>
										</>
									)}
								</ETypo>
								<MultiSelect
									{...{
										placeholder: "Select justification options",
										Option: justificationOptions,
										searchVisibility: false,
										multiSelect: true,
										dropDownValue: "name",
										onChange: onJustificationOptionsChange,
										onClear: () => onJustificationOptionsChange([]),
										className: "justification-multiselect"
									}}
								/>
							</div>

							{requiredDocumentsList.length > 0 && (
								<div className="required-documents-box">
									<div className="required-documents-content">
										<ImageComponent src={icons.focus_state.Warning} className="warning-icon" />
										<div className="required-documents-text">
											<ETypo b2 bold className="required-documents-title">
												Documents to Upload:{' '}
											</ETypo>
											<ETypo b2 className="required-documents-list">
												{requiredDocumentsList.join(' | ')}
											</ETypo>
										</div>
									</div>
								</div>
							)}
						</div>

						<div className="form-right">
							<div className="upload-area">
								<div className="upload-header">
									Upload any Proof of justification.
									{requiresJustificationDocument && (
										<ETypo b3 medium color="#ff4d4f" style={{ marginLeft: '4px', display: 'inline' }}>
											*
										</ETypo>
									)}
								</div>
								<MultiUploadDragger
									previousFiles={previousJustificationDocuments}
									currentFiles={justificationFiles}
									onChange={onFileUpload}
									onRemoveUpload={onFileRemove}
									onDownload={onDownloadDocument}
									acceptedTypes=".pdf,.doc,.docx,.jpg,.jpeg,.png,.JPG,.JPEG,.PNG,.PDF,.DOC,.DOCX"
									idleText1="Drag and drop or click to upload"
									idleText2="multiple files"
									fileMaxSize="Maximum file size: 5MB per file"
									className="justification-dragger"
								/>
								{justificationFilesError && (
									<ETypo b3 className="error-msg" style={{ color: '#ff4d4f', marginTop: '8px' }}>
										{justificationFilesError}
									</ETypo>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ComplianceViolationsPresentational;
